import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RadioGroup from "@material-ui/core/FormGroup";
import Radio from "@material-ui/core/Radio";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";

import { validateVersion } from "../../helpers/validations";
import useInput from "../../hooks/use-input";
import { addLicense, updateLicense } from "../../store/licenses-actions";

const EditLicenseDialog = (props) => {
  const { customerId, isAdding } = props;
  const license = useSelector((state) => state.licenses.selectedLicense);

  const dispatch = useDispatch();

  const {
    value: version,
    isValid: isVersionValid,
    hasError: hasVersionInputError,
    inputBlurHandler: versionBlurHandler,
    inputChangeHandler: versionChangeHandler,
    reset: versionReset,
    touch: versionTouch,
  } = useInput(validateVersion);

  const isFormInalid = !isVersionValid;

  const [id, setId] = useState("");
  const [maxResearches, setMaxResearches] = useState(1);
  const [maxUsers, setMaxUsers] = useState(1);
  const [maxSubjects, setMaxSubjects] = useState(1);
  const [maxCrfs, setMaxCrfs] = useState(1);
  const [expiryDate, setExpiryDate] = useState(null);
  const [licenseType, setLicenseType] = useState("Standard");

  useEffect(() => {
    setId(!isAdding ? license.id : null);
    versionChangeHandler({
      target: { value: !isAdding ? license.version : "" },
    });
    setMaxResearches(!isAdding ? license.maxResearches : 1);
    setMaxUsers(!isAdding ? license.maxUsers : 1);
    setMaxSubjects(!isAdding ? license.maxSubjects : 1);
    setMaxCrfs(!isAdding ? license.maxCrfs : 1);
    setExpiryDate(!isAdding ? license.expires : null);
    setLicenseType(!isAdding ? license.licenseType : "Standard");
  }, [license, isAdding, versionChangeHandler]);

  const saveButtonClickHandler = () => {
    versionTouch();

    if (isFormInalid) {
      return;
    }

    const method = isAdding ? addMethod : updateMethod;
    method();
    handleClose();
  };

  const addMethod = async () => {
    dispatch(
      addLicense(customerId, {
        version,
        maxResearches,
        maxUsers,
        maxSubjects,
        maxCrfs,
        expires: expiryDate,
      })
    );
  };

  const updateMethod = async () => {
    dispatch(
      updateLicense({
        id,
        version,
        maxResearches,
        maxUsers,
        maxSubjects,
        maxCrfs,
        expires: expiryDate,
      })
    );
  };

  const formatDate = (date) => {    
    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');

    return `${yyyy}-${MM}-${dd}`;
  }

  const changeDateHandler = (newDate) =>
    setExpiryDate(newDate ? formatDate(newDate) : null);

  const handleClose = () => {
    versionReset();
    props.onClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.close}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">Add new license</DialogTitle>
      <DialogContent>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <RadioGroup
            row
            aria-label="position"
            name="position"
            defaultValue="top"
          >
            <FormControlLabel
              value="Standard"
              checked={licenseType === "Standard"}
              control={<Radio color="primary" />}
              onChange={() => setLicenseType("Standard")}
              label="Standard"
              labelPlacement="end"
            />
            <FormControlLabel
              value="Trial"
              checked={licenseType === "Trial"}
              control={<Radio color="primary" />}
              onChange={() => setLicenseType("Trial")}
              label="Trial"
              labelPlacement="end"
            />
          </RadioGroup>

          {licenseType === "Standard" && (
            <KeyboardDatePicker
            autoFocus
              disableToolbar
              fullWidth
              autoOk
              variant="inline"
              format="dd.MM.yyyy"
              margin="normal"
              id="date-picker-inline"
              label="Expiry date"
              value={expiryDate}
              onChange={changeDateHandler}
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
            />
          )}

          <TextField            
            margin="dense"
            id="version"
            label="Version"
            type="text"
            fullWidth
            value={version}
            error={hasVersionInputError}
            helperText={
              hasVersionInputError
                ? "Incorrect format. Version should be something like one of these: 3.21, <3.21, 3.*, *"
                : null
            }
            onBlur={versionBlurHandler}
            onChange={versionChangeHandler}
          />
          <TextField
            margin="dense"
            id="maxResearches"
            label="Max researches"
            type="number"
            min="1"
            fullWidth
            value={maxResearches}
            onChange={(event) => setMaxResearches(event.target.value)}
          />
          <TextField
            margin="dense"
            id="maxUsers"
            label="Max users"
            type="number"
            min="1"
            fullWidth
            value={maxUsers}
            onChange={(event) => setMaxUsers(event.target.value)}
          />
          <TextField
            margin="dense"
            id="maxSubjects"
            label="Max subjects"
            type="number"
            min="1"
            fullWidth
            value={maxSubjects}
            onChange={(event) => setMaxSubjects(event.target.value)}
          />
          <TextField
            margin="dense"
            id="maxCrfs"
            label="Max crfs"
            type="number"
            min="1"
            fullWidth
            value={maxCrfs}
            onChange={(event) => setMaxCrfs(event.target.value)}
          />
        </MuiPickersUtilsProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={saveButtonClickHandler} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default EditLicenseDialog;
